/**
 * Task action mutations with offline-first support.
 * Time entries are stored locally first, then synced when online.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { startTask, stopTask, completeTask, startSerializedTask, ApiError, type StartSerializedResponse } from '../lib/api';
import { addOfflineTimeEntry, getDB } from '../lib/db';

// Active entry is persisted to sessionStorage so stop/complete works after page reload.
const ACTIVE_ENTRY_KEY = 'scheduler-active-entry';

interface ActiveEntry {
  clientEntryId: string;
  taskId: string;
  startTime: string;
}

let activeEntry: ActiveEntry | null = null;

// Recover from sessionStorage on module load
try {
  const stored = sessionStorage.getItem(ACTIVE_ENTRY_KEY);
  if (stored) activeEntry = JSON.parse(stored);
} catch {
  // sessionStorage unavailable (SSR, private browsing, etc.)
}

function setActiveEntry(entry: ActiveEntry | null) {
  activeEntry = entry;
  try {
    if (entry) {
      sessionStorage.setItem(ACTIVE_ENTRY_KEY, JSON.stringify(entry));
    } else {
      sessionStorage.removeItem(ACTIVE_ENTRY_KEY);
    }
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Start a task - creates time entry in IndexedDB first (offline-first).
 */
export function useStartTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const deviceTimestamp = new Date().toISOString();
      const clientEntryId = uuidv4();
      const deviceTimeOffsetSeconds = new Date().getTimezoneOffset() * 60;

      // Store in IndexedDB first (offline-first)
      await addOfflineTimeEntry({
        clientEntryId,
        taskId,
        startTime: deviceTimestamp,
        endTime: null,
        deviceTimeOffsetSeconds,
      });

      // Track active entry for stop/complete (persisted to sessionStorage)
      setActiveEntry({
        clientEntryId,
        taskId,
        startTime: deviceTimestamp,
      });

      // Try API call (may fail if offline)
      try {
        return await startTask(taskId, deviceTimestamp);
      } catch (error) {
        // If offline, that's OK - entry is stored locally
        if (error instanceof ApiError && error.status === 0) {
          return { data: { id: taskId, status: 'in_progress' } };
        }
        throw error;
      }
    },
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Stop a task - updates time entry end time in IndexedDB.
 */
export function useStopTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const deviceTimestamp = new Date().toISOString();

      // Update local entry with end time
      if (activeEntry && activeEntry.taskId === taskId) {
        const db = await getDB();
        const entry = await db.get('timeEntries', activeEntry.clientEntryId);
        if (entry) {
          await db.put('timeEntries', {
            ...entry,
            endTime: deviceTimestamp,
          });
        }
        setActiveEntry(null);
      }

      // Try API call
      try {
        return await stopTask(taskId, deviceTimestamp);
      } catch (error) {
        if (error instanceof ApiError && error.status === 0) {
          return { data: { id: taskId, status: 'ready' } };
        }
        throw error;
      }
    },
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Complete a task - updates time entry end time in IndexedDB.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const deviceTimestamp = new Date().toISOString();

      // Update local entry with end time
      if (activeEntry && activeEntry.taskId === taskId) {
        const db = await getDB();
        const entry = await db.get('timeEntries', activeEntry.clientEntryId);
        if (entry) {
          await db.put('timeEntries', {
            ...entry,
            endTime: deviceTimestamp,
          });
        }
        setActiveEntry(null);
      }

      // Try API call
      try {
        return await completeTask(taskId, deviceTimestamp);
      } catch (error) {
        if (error instanceof ApiError && error.status === 0) {
          return { data: { id: taskId, status: 'complete' } };
        }
        throw error;
      }
    },
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Get the current active entry (for timer display).
 */
export function getActiveEntry() {
  return activeEntry;
}

/**
 * Start a serialized task - assigns serial number and starts build.
 * REQUIRES CONNECTIVITY - throws if offline.
 */
export function useStartSerializedTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string): Promise<StartSerializedResponse & { taskId: string }> => {
      // This requires connectivity - no offline fallback
      const result = await startSerializedTask(taskId);

      // Also create the time entry for tracking
      const deviceTimestamp = new Date().toISOString();
      const clientEntryId = uuidv4();
      const deviceTimeOffsetSeconds = new Date().getTimezoneOffset() * 60;

      // Store in IndexedDB
      await addOfflineTimeEntry({
        clientEntryId,
        taskId,
        startTime: deviceTimestamp,
        endTime: null,
        deviceTimeOffsetSeconds,
      });

      // Track active entry for stop/complete (persisted to sessionStorage)
      setActiveEntry({
        clientEntryId,
        taskId,
        startTime: deviceTimestamp,
      });

      return { ...result, taskId };
    },
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}
